import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr, fmtDateShort } from "@/lib/api";
import { roleLabel } from "@/lib/auth";
import { EnvelopeSimple, Plus, X, Copy, Check, Trash, UserCheck, UserMinus, Clock } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700",
  expired: "bg-slate-200 text-slate-600",
  revoked: "bg-rose-100 text-rose-700",
};

export default function Invites() {
  const qc = useQueryClient();
  const { data: invites = [] } = useQuery({ queryKey: ["invites"], queryFn: () => http.get("/invites").then((r) => r.data) });
  const { data: pending = [] } = useQuery({ queryKey: ["pending-users"], queryFn: () => http.get("/users/pending").then((r) => r.data) });
  const { data: rolesData } = useQuery({ queryKey: ["roles"], queryFn: () => http.get("/roles").then((r) => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then((r) => r.data) });

  const roleOptions = [
    ...((rolesData?.builtins || []).map((b) => ({ slug: b.slug, label: b.label }))),
    ...((rolesData?.customs || []).map((c) => ({ slug: c.slug, label: c.label }))),
  ];

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ email: "", role: "contractor", store_id: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [createdLink, setCreatedLink] = useState("");
  const [copiedId, setCopiedId] = useState("");

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(""), 1500);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setCreatedLink("");
    try {
      const body = { email: form.email, role: form.role, send_email: form.send_email, store_id: form.store_id || null };
      const r = await http.post("/invites", body);
      setCreatedLink(r.data.link);
      qc.invalidateQueries({ queryKey: ["invites"] });
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openModal = () => { setForm({ email: "", role: "contractor", store_id: "", send_email: true }); setErr(""); setCreatedLink(""); setModal(true); };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try { await http.delete(`/invites/${id}`); qc.invalidateQueries({ queryKey: ["invites"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  const approve = async (id) => {
    try { await http.post(`/users/${id}/approve`); qc.invalidateQueries({ queryKey: ["pending-users"] }); qc.invalidateQueries({ queryKey: ["users"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };
  const reject = async (id, name) => {
    if (!window.confirm(`Reject ${name}'s registration? They will not be able to sign in.`)) return;
    try { await http.post(`/users/${id}/reject`); qc.invalidateQueries({ queryKey: ["pending-users"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Team</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Invites & approvals</h1>
          <p className="text-sm text-slate-600 mt-1">Invite people to register, then approve their account before it goes live.</p>
        </div>
        <button onClick={openModal} data-testid="invite-new-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> Invite user
        </button>
      </div>

      {/* Pending approvals */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-amber-500" />
          <h2 className="text-sm font-heading uppercase tracking-wider text-slate-600">Pending approvals</h2>
          {pending.length > 0 && <span className="text-[11px] font-mono-data bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">{pending.length}</span>}
        </div>
        {pending.length === 0 ? (
          <div className="rmms-card p-5 text-sm text-slate-500" data-testid="pending-empty">No registrations awaiting approval.</div>
        ) : (
          <div className="rmms-card overflow-hidden" data-testid="pending-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Registered</th><th className="px-4 py-2.5 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {pending.map((u) => (
                  <tr key={u.id} data-testid={`pending-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 font-mono-data text-xs">{u.email}</td>
                    <td className="px-4 py-3">{roleLabel(u.role)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDateShort(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 justify-end">
                        <button onClick={() => approve(u.id)} data-testid={`approve-user-${u.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700">
                          <UserCheck size={13} weight="bold" /> Approve
                        </button>
                        <button onClick={() => reject(u.id, u.name)} data-testid={`reject-user-${u.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium border border-slate-200 text-rose-600 hover:bg-rose-50">
                          <UserMinus size={13} weight="bold" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invites */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <EnvelopeSimple size={16} className="text-slate-500" />
          <h2 className="text-sm font-heading uppercase tracking-wider text-slate-600">Invitations</h2>
        </div>
        {invites.length === 0 ? (
          <div className="rmms-card p-5 text-sm text-slate-500" data-testid="invites-empty">No invitations yet. Use the Invite user button to send one.</div>
        ) : (
          <div className="rmms-card overflow-hidden" data-testid="invites-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Role</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Sent</th><th className="px-4 py-2.5 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {invites.map((inv) => (
                  <tr key={inv.id} data-testid={`invite-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono-data text-xs">{inv.email}</td>
                    <td className="px-4 py-3">{roleLabel(inv.role)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-heading uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLE[inv.status] || "bg-slate-100 text-slate-600"}`}>{inv.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDateShort(inv.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 justify-end">
                        {inv.status === "pending" && (
                          <button onClick={() => copy(inv.link, inv.id)} data-testid={`copy-invite-${inv.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium border border-slate-200 hover:bg-slate-50">
                            {copiedId === inv.id ? <><Check size={13} weight="bold" className="text-emerald-500" /> Copied</> : <><Copy size={13} /> Copy link</>}
                          </button>
                        )}
                        {(inv.status === "pending" || inv.status === "expired") && (
                          <button onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.id}`}
                            className="h-8 w-8 border border-slate-200 rounded-md flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="invite-modal">
          <div className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="overline">New</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Invite user</h2></div>
              <button type="button" onClick={() => setModal(false)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            {!createdLink ? (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="overline mb-1.5 block">Email *</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" className="rmms-input" />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Role *</label>
                  <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role-select" className="rmms-input">
                    {roleOptions.map((r) => <option key={r.slug} value={r.slug}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline mb-1.5 block">Home store (optional)</label>
                  <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} data-testid="invite-store-select" className="rmms-input">
                    <option value="">(none)</option>
                    {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="invite-sendemail-checkbox" />
                  Email the invite link to them
                </label>
                {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid="invite-error">{err}</div>}
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                  <button type="button" onClick={() => setModal(false)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="invite-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                    {busy ? "Creating…" : "Create invite"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4" data-testid="invite-created">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <Check size={18} weight="bold" /> Invite created{form.send_email ? " and emailed" : ""}.
                </div>
                <div>
                  <label className="overline mb-1.5 block">Shareable link</label>
                  <div className="flex gap-2">
                    <input readOnly value={createdLink} data-testid="invite-link-output" className="rmms-input font-mono-data text-xs" />
                    <button onClick={() => copy(createdLink, "new")} data-testid="invite-link-copy"
                      className="shrink-0 h-10 px-3 rounded-md border border-slate-200 text-sm inline-flex items-center gap-1 hover:bg-slate-50">
                      {copiedId === "new" ? <><Check size={14} className="text-emerald-500" /> Copied</> : <><Copy size={14} /> Copy</>}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                  <button type="button" onClick={openModal} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Invite another</button>
                  <button type="button" onClick={() => setModal(false)} className="h-10 px-4 rounded-md text-white text-sm" style={{ background: "#002FA7" }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
